import { Table } from "antd"
import type { ColumnsType } from "antd/es/table"
import { useCallback, useMemo, useState } from "react"

import { selectTableTabColumns } from "../containers/User/redux/ui-preferences.selectors.ts"
import {
  patchTablePrefs,
  resetTabPrefs
} from "../containers/User/redux/ui-preferences.thunks.ts"
import type {
  ColumnConfigItem,
  TabKey
} from "../containers/User/redux/ui-preferences.types.ts"
import { useAppDispatch } from "./use-app-dispatch.ts"
import { useAppSelector } from "./use-app-selector.ts"

// `Table.EXPAND_COLUMN` is not a column but a marker saying where AntD should
// put the expand control. AntD finds it by identity, so it has to keep both its
// position and its object reference: moved to the end it drags the expand
// control along with it, and copied it stops being recognised at all and
// renders as an unnamed empty column.
export const isExpandColumn = (col: unknown): boolean =>
  col === Table.EXPAND_COLUMN

function keyedColumns<T>(cols: ColumnsType<T>): ColumnsType<T> {
  return cols.filter(
    (c) => !isExpandColumn(c) && c.key !== undefined && c.key !== null
  )
}

// Saved keys keep their order; a key the saved config never mentioned is spliced
// in at its canonical index rather than appended, so a column that only just
// became customisable keeps its default position for users with existing prefs.
function mergeKeyOrder(canonicalKeys: string[], savedKeys: string[]): string[] {
  const canonical = new Set(canonicalKeys)
  // A saved layout naming the same column twice would otherwise lay it out
  // twice: `seen` is built before the splice loop, so the repeat is never
  // noticed.
  const order = Array.from(
    new Set(savedKeys.filter((key) => canonical.has(key)))
  )
  const seen = new Set(order)
  canonicalKeys.forEach((key, canonicalIndex) => {
    if (!seen.has(key)) order.splice(canonicalIndex, 0, key)
  })
  return order
}

const NO_COLUMNS: readonly string[] = []

// Filters hidden columns out and reorders the rest to the saved order, placing
// any column the saved config never mentioned at its canonical index — visible
// unless the table lists it in `hiddenByDefault`, which only a saved entry can
// override. Keyless columns (e.g. action columns) are always-visible
// passthroughs that never participate in the saved config.
export function applyConfig<T>(
  fullColumns: ColumnsType<T>,
  config: ColumnConfigItem[] | null,
  defaultHiddenColumns: readonly string[] = NO_COLUMNS
): ColumnsType<T> {
  const defaultHidden = new Set(defaultHiddenColumns)
  if (!config) {
    if (defaultHidden.size === 0) return fullColumns
    return fullColumns.filter(
      (c) => isExpandColumn(c) || !defaultHidden.has(String(c.key))
    )
  }

  const keyed = keyedColumns(fullColumns)
  const keyless = fullColumns.filter(
    (c) => !isExpandColumn(c) && (c.key === undefined || c.key === null)
  )
  const expandAt = fullColumns.findIndex(isExpandColumn)
  const byKey = new Map(keyed.map((c) => [String(c.key), c]))
  const entryByKey = new Map(config.map((e) => [e.key, e]))

  const ordered: ColumnsType<T> = []
  for (const key of mergeKeyOrder(
    keyed.map((c) => String(c.key)),
    config.map((e) => e.key)
  )) {
    const entry = entryByKey.get(key)
    if (entry ? !entry.visible : defaultHidden.has(key)) continue
    const col = byKey.get(key)
    // A saved layout can name a column this table no longer has — renamed,
    // removed, or belonging to another surface. Spreading the missing column
    // while keeping the saved width left `{ width }` on its own: a column with
    // no title and no cell content.
    if (!col) continue
    ordered.push(entry?.width != null ? { ...col, width: entry.width } : col)
  }
  ordered.push(...keyless)
  // Put the marker back where the table declared it, as the same object.
  if (expandAt >= 0) ordered.splice(expandAt, 0, fullColumns[expandAt])
  return ordered
}

export function useTableColumnConfig<T>(
  tableKey: string,
  tabKey: TabKey,
  fullColumns: ColumnsType<T>,
  defaultHiddenColumns: readonly string[] = NO_COLUMNS,
  // A caller that shares a real surface key without being a real, persisted
  // table (a tour/demo preview rendering the production table on fake data)
  // must never read or write that surface's real saved layout — `persist:
  // false` keeps this instance on its own in-memory config for the life of
  // the component instead of touching redux/the API at all.
  persist = true
) {
  const dispatch = useAppDispatch()
  const storedConfig = useAppSelector((s) =>
    selectTableTabColumns(s, tableKey, tabKey)
  )
  const [localConfig, setLocalConfig] = useState<ColumnConfigItem[] | null>(
    null
  )
  const config = persist ? storedConfig : localConfig

  const columns = useMemo(
    () => applyConfig(fullColumns, config, defaultHiddenColumns),
    [fullColumns, config, defaultHiddenColumns]
  )

  // Mirrors applyConfig's ordering: this drives the customisation modal and is
  // what gets persisted on resize/drag/save, so the two must not disagree.
  const fullConfig = useMemo<ColumnConfigItem[]>(() => {
    const saved = config ?? []
    const entryByKey = new Map(saved.map((e) => [e.key, e]))
    const defaultHidden = new Set(defaultHiddenColumns)
    return mergeKeyOrder(
      keyedColumns(fullColumns).map((c) => String(c.key)),
      saved.map((e) => e.key)
    ).map(
      (key) => entryByKey.get(key) ?? { key, visible: !defaultHidden.has(key) }
    )
  }, [fullColumns, config, defaultHiddenColumns])

  const setColumns = useCallback(
    (next: ColumnConfigItem[]) => {
      if (!persist) {
        setLocalConfig(next)
        return
      }
      dispatch(patchTablePrefs({ tableKey, tabKey, columns: next }))
    },
    [dispatch, tableKey, tabKey, persist]
  )

  const resetToDefaults = useCallback(() => {
    if (!persist) {
      setLocalConfig(null)
      return
    }
    dispatch(resetTabPrefs({ tableKey, tabKey }))
  }, [dispatch, tableKey, tabKey, persist])

  return { columns, fullConfig, setColumns, resetToDefaults }
}
